import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AdminController, AdminWorkspace } from '../../controllers/admin'
import IdLookup from '../components/IdLookup'

export default function PeppolPage() {
  const { t } = useTranslation()
  const [workspace, setWorkspace] = React.useState<AdminWorkspace | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  const loadWorkspace = async (id: string) => {
    setLoading(true)
    setWorkspace(null)
    try {
      setWorkspace(await AdminController.getWorkspace(id))
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const deactivate = async () => {
    setLoading(true)
    try {
      const result = await AdminController.deactivatePeppol(workspace.id)
      setWorkspace(result.workspace)
      setConfirmOpen(false)
      toast.success(t('admin::Peppol deactivated'))
      if (result.warning) toast.warning(result.warning)
    } catch (error) {
      toast.error(error.message)
      setConfirmOpen(false)
      setWorkspace(await AdminController.getWorkspace(workspace.id).catch(() => workspace))
    } finally {
      setLoading(false)
    }
  }

  const canDeactivate = workspace && (workspace.peppol_identifiers.length > 0 || workspace.peppol_status)

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-xl font-semibold">{t('admin::Peppol')}</h1>
      <IdLookup label={t('admin::Workspace ID')} loading={loading} onSubmit={loadWorkspace} />

      {workspace && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {workspace.business_name}
              <Badge variant="secondary">{workspace.peppol_status || t('admin::Not registered')}</Badge>
            </CardTitle>
            <CardDescription>{workspace.vat_number}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {workspace.peppol_identifiers.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin::Scheme')}</TableHead>
                    <TableHead>{t('admin::Endpoint ID')}</TableHead>
                    <TableHead>{t('admin::Peppol ID')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workspace.peppol_identifiers.map((identifier) => (
                    <TableRow key={identifier.id}>
                      <TableCell>{identifier.scheme}</TableCell>
                      <TableCell>{identifier.endpoint_id}</TableCell>
                      <TableCell className="font-mono">{identifier.peppol_id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">{t('admin::No active Peppol IDs.')}</p>
            )}
            <Button variant="destructive" className="self-start" disabled={!canDeactivate || loading} onClick={() => setConfirmOpen(true)}>
              {t('admin::Deactivate Peppol')}
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin::Deactivate Peppol?')}</DialogTitle>
            <DialogDescription>
              {t('admin::This unregisters all Peppol IDs for {{name}}, resets the Peppol status and removes the Peppol item from the subscription.', { name: workspace?.business_name, interpolation: { escapeValue: false } })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>{t('admin::Cancel')}</DialogClose>
            <Button variant="destructive" onClick={deactivate} disabled={loading}>
              {t('admin::Deactivate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
